/**
 * Video Config Parser
 *
 * Extracts VIDEO_CONFIG JSON blocks from AI assistant responses
 * and converts them to Partial<VideoConfig> for form population.
 *
 * Supports multiple formats since LLMs may not always use the exact marker:
 * 1. ```json:VIDEO_CONFIG\n{...}\n```  (preferred)
 * 2. ```json\n{...}\n```               (common LLM output)
 * 3. ```\n{...}\n```                   (generic code block)
 */

import type { VideoConfig } from '@/types';

/** Pattern 1: Explicit VIDEO_CONFIG marker (preferred) */
const VIDEO_CONFIG_REGEX = /```json:VIDEO_CONFIG\s*\r?\n([\s\S]*?)\r?\n\s*```/;

/** Pattern 2: Plain ```json code blocks (lenient: optional spaces, \r\n) */
const PLAIN_JSON_REGEX = /```json\s*\r?\n([\s\S]*?)\r?\n\s*```/;

/** Pattern 3: Generic code blocks containing a JSON object */
const GENERIC_CODE_REGEX = /```\s*\r?\n(\{[\s\S]*?\})\s*\r?\n\s*```/;

/** Pattern 4: Raw JSON object with "topic" field (no code block - last resort) */
const RAW_JSON_REGEX = /(\{\s*"topic"\s*:\s*"[\s\S]*?\n\})/;

/** Valid fields that can be set from the assistant — must match VideoConfig keys */
const VALID_CONFIG_FIELDS = new Set<string>([
  // Core
  'topic',
  'duration_minutes',
  'resolution',
  'fps',
  'language',
  'style',

  // Narration & TTS
  'narration_mode',
  'user_narration_text',
  'tts_provider',
  'voice_id',
  'phonetic_correction',
  'adjust_timing_to_narration',

  // Subtitles
  'subtitles',
  'subtitle_mode',
  'subtitle_languages',
  'subtitle_style',
  'emoticon_subtitles',
  'translation_provider',

  // Media sources
  'media_type',
  'image_source',
  'image_provider',
  'video_source',

  // AI image (Fooocus)
  'fooocus_style_mode',
  'fooocus_styles',
  'fooocus_style_description',

  // AI video provider configs
  'veo_config',
  'kling_config',
  'vidu_config',
  'seedance2_config',

  // Audio & effects
  'audio_languages',
  'effects_config',

  // LLM
  'llm_provider',
  'llm_model',
]);

/**
 * Find the first JSON config block in the content.
 * Tries multiple patterns in order of specificity.
 * Returns the regex match and parsed JSON, or null if not found.
 */
function findConfigBlock(
  content: string
): { regex: RegExp; json: string; parsed: Record<string, unknown> } | null {
  const patterns: RegExp[] = [
    VIDEO_CONFIG_REGEX,
    PLAIN_JSON_REGEX,
    GENERIC_CODE_REGEX,
    RAW_JSON_REGEX,
  ];

  for (const regex of patterns) {
    const match = content.match(regex);
    if (!match?.[1]) continue;

    try {
      const parsed = JSON.parse(match[1]);
      // Must be an object with a "topic" field to be a valid video config
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) && parsed.topic) {
        return { regex, json: match[1], parsed };
      }
    } catch {
      // Invalid JSON, try next pattern
    }
  }

  return null;
}

/**
 * Extract a VideoConfig from an AI assistant response.
 * Returns null if no valid config block is found.
 */
export function extractVideoConfig(content: string): Partial<VideoConfig> | null {
  const found = findConfigBlock(content);
  if (!found) {
    if (content.includes('"topic"')) {
      console.warn(
        '[VideoConfigParser] Content has "topic" but no config block matched. Content preview:',
        content.substring(0, 300)
      );
    }
    return null;
  }

  // Filter to only valid VideoConfig fields
  const config: Partial<VideoConfig> = {};
  for (const [key, value] of Object.entries(found.parsed)) {
    if (VALID_CONFIG_FIELDS.has(key) && value !== undefined && value !== null) {
      (config as Record<string, unknown>)[key] = value;
    }
  }

  // Must have at least a topic to be considered valid
  if (!config.topic) return null;

  // --- Normalize fields that LLMs often return with slight formatting issues ---

  // Resolution: Unicode multiply sign (U+00D7) → ASCII x
  if (config.resolution && typeof config.resolution === 'string') {
    config.resolution = config.resolution.replace(/\u00d7/g, 'x').trim();
  }

  // Language: lowercase + trim (LLMs may return "En-US" or "pt-BR ")
  if (config.language && typeof config.language === 'string') {
    config.language = config.language.trim().toLowerCase();
  }

  // Style: trim whitespace
  if (config.style && typeof config.style === 'string') {
    config.style = config.style.trim().toLowerCase();
  }

  // Video source: trim + lowercase
  if (config.video_source && typeof config.video_source === 'string') {
    config.video_source = config.video_source.trim().toLowerCase();
  }

  // Image source: trim + lowercase
  if (config.image_source && typeof config.image_source === 'string') {
    config.image_source = config.image_source.trim().toLowerCase();
  }

  // Narration mode: trim + lowercase
  if (config.narration_mode && typeof config.narration_mode === 'string') {
    config.narration_mode = config.narration_mode.trim().toLowerCase();
  }

  // TTS provider: trim + lowercase
  if (config.tts_provider && typeof config.tts_provider === 'string') {
    config.tts_provider = config.tts_provider.trim().toLowerCase();
  }

  // Media type: trim + lowercase
  if (config.media_type && typeof config.media_type === 'string') {
    config.media_type = config.media_type.trim().toLowerCase();
  }

  // duration_minutes: ensure it's a number (LLMs sometimes return "2.0" as string)
  if (config.duration_minutes && typeof config.duration_minutes === 'string') {
    const parsed = parseFloat(config.duration_minutes);
    if (!isNaN(parsed)) {
      (config as Record<string, unknown>).duration_minutes = parsed;
    }
  }

  // fps: ensure it's a number
  if (config.fps && typeof config.fps === 'string') {
    const parsed = parseInt(config.fps as unknown as string, 10);
    if (!isNaN(parsed)) {
      (config as Record<string, unknown>).fps = parsed;
    }
  }

  return config;
}

/**
 * Remove the config block from a message, leaving only the human-readable text.
 */
export function removeVideoConfigBlock(content: string): string {
  const found = findConfigBlock(content);
  if (!found) return content.trim();

  return content.replace(found.regex, '').trim();
}
